'use client'

import { ReactNode } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { ShieldX, HomeIcon } from 'lucide-react'
import { useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import { Skeleton } from '@/components/ui/skeleton'

type PropsType = {
  children: ReactNode
  permission: string
  action: string
  fallback?: ReactNode
}

/**
 * Permission Guard Component
 * Wraps content and only renders children if the user has the required permission
 * Otherwise shows 403 forbidden page that takes over the entire screen
 */
const PermissionGuard = ({ children, permission, action, fallback }: PropsType) => {
  const t = useTranslations()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const isLoading = meState.isLoading

  // Show loading state while fetching user data
  if (isLoading || !currentUser) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  // Check if the user has the required permission
  const hasRequiredPermission = hasPermission(currentUser, permission, action)

  if (!hasRequiredPermission) {
    // Show custom fallback if provided
    if (fallback) {
      return <>{fallback}</>
    }

    // Show full-page 403 forbidden error
    return (
      <div className="bg-background fixed inset-0 z-9999 flex min-h-screen items-center px-4 py-12 sm:px-6 md:px-8 lg:px-12 xl:px-16">
        <div className="w-full space-y-6 text-center">
          <div className="space-y-3">
            <div className="mb-4 flex justify-center text-red-500">
              <ShieldX className="h-20 w-20" />
            </div>
            <h1 className="animate-bounce text-4xl font-bold tracking-tighter sm:text-5xl">403</h1>
            <h2 className="mb-5 text-2xl font-semibold tracking-tight sm:text-3xl">{t('access_denied')}</h2>
            <p className="text-gray-500">{t('access_denied_description')}</p>
          </div>
          <Link
            href="/"
            className="inline-flex h-10 items-center rounded-md bg-gray-900 px-8 text-sm font-medium text-gray-50 shadow transition-colors hover:bg-gray-900/90 focus-visible:ring-1 focus-visible:ring-gray-950 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 dark:bg-gray-50 dark:text-gray-900 dark:hover:bg-gray-50/90 dark:focus-visible:ring-gray-300">
            <HomeIcon className="mr-2 h-4 w-4" />
            {t('return_to_home')}
          </Link>
        </div>
      </div>
    )
  }

  // User has permission, render children
  return <>{children}</>
}

export default PermissionGuard
